import { format, parseISO } from 'date-fns';
// Import worker code as raw string to bypass Vite's worker bundling plugin
import pdfWorkerScript from '../workers/pdfAssemble.worker.js?raw';
import jsPdfUmd from 'jspdf/dist/jspdf.umd.min.js?raw';

/**
 * Sanitizes a string for use in filenames
 */
const sanitizeFilename = (str) => {
  return str.replace(/[<>:"/\\|?*]/g, '').trim();
};

/**
 * Create and download a file from ArrayBuffer
 */
const downloadFile = (arrayBuffer, filename) => {
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  
  // Create temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate filename for the PDF
 */
const generateFilename = (studentName, lessonDateISO) => {
  let formattedDate = 'Unknown-Date';
  try {
    if (lessonDateISO) {
      const date = parseISO(lessonDateISO);
      formattedDate = format(date, 'yyyy-MM-dd');
    }
  } catch (error) {
    console.warn('Could not parse lesson date:', lessonDateISO);
  }
  
  const sanitizedStudentName = sanitizeFilename(studentName || 'Unknown-Student');
  return `LessonNote_${sanitizedStudentName}_${formattedDate}.pdf`;
};

/**
 * Yield control to keep UI responsive
 */
const yieldControl = () => {
  return new Promise(resolve => {
    requestAnimationFrame(resolve);
  });
};

/**
 * Remove problematic elements that could trigger network requests
 */
const removeProblematicElements = (element) => {
  // Remove iframes (tools) as they can trigger auth requests
  const iframes = element.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    const placeholder = document.createElement('div');
    placeholder.style.cssText = `
      width: 100%;
      height: 200px;
      border: 2px dashed #d1d5db;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f9fafb;
      color: #6b7280;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      margin: 1rem 0;
      border-radius: 8px;
    `;
    placeholder.textContent = '[Interactive Tool - Not shown in PDF]';
    iframe.parentNode.replaceChild(placeholder, iframe);
  });

  // Remove script tags that could execute
  const scripts = element.querySelectorAll('script');
  scripts.forEach(script => script.remove());

  // Remove any elements with data attributes that might trigger React effects
  const reactElements = element.querySelectorAll('[data-reactroot], [data-react-checksum]');
  reactElements.forEach(el => {
    el.removeAttribute('data-reactroot');
    el.removeAttribute('data-react-checksum');
  });

  // Remove any elements that might have event listeners or cause side effects
  const interactiveElements = element.querySelectorAll('button, input, select, textarea');
  interactiveElements.forEach(el => {
    // Convert buttons to spans to preserve text content
    if (el.tagName === 'BUTTON') {
      const span = document.createElement('span');
      span.textContent = el.textContent;
      span.className = el.className;
      // Copy basic styles
      const computedStyle = window.getComputedStyle(el);
      span.style.cssText = `
        display: inline-block;
        padding: ${computedStyle.padding};
        background-color: ${computedStyle.backgroundColor};
        color: ${computedStyle.color};
        border: ${computedStyle.border};
        border-radius: ${computedStyle.borderRadius};
        font-family: ${computedStyle.fontFamily};
        font-size: ${computedStyle.fontSize};
        font-weight: ${computedStyle.fontWeight};
      `;
      el.parentNode.replaceChild(span, el);
    } else {
      // For other interactive elements, just remove them
      el.remove();
    }
  });

  // Remove any elements that might contain React components or cause re-renders
  const componentElements = element.querySelectorAll('[data-testid], [data-cy]');
  componentElements.forEach(el => {
    el.removeAttribute('data-testid');
    el.removeAttribute('data-cy');
  });

  // Remove any SVG elements that might have complex interactions
  const svgs = element.querySelectorAll('svg');
  svgs.forEach(svg => {
    // Convert SVG icons to simple text representations
    const iconText = svg.getAttribute('aria-label') || svg.getAttribute('title') || '•';
    const textNode = document.createTextNode(iconText);
    svg.parentNode.replaceChild(textNode, svg);
  });

  return element;
};

/**
 * Prepare content for rendering by cloning and styling
 */
const prepareContentForRender = (contentElement) => {
  if (!contentElement) {
    throw new Error('Content element is required for PDF export');
  }
  
  // Clone the element to avoid modifying the original
  const clone = contentElement.cloneNode(true);
  
  // Remove problematic elements that could trigger network requests or auth
  const cleanedClone = removeProblematicElements(clone);
  
  // Create offscreen container with A4 dimensions and styling
  const offscreenContainer = document.createElement('div');
  offscreenContainer.style.position = 'absolute';
  offscreenContainer.style.left = '-9999px';
  offscreenContainer.style.top = '0';
  offscreenContainer.style.width = '210mm'; // A4 width
  offscreenContainer.style.backgroundColor = 'white';
  offscreenContainer.style.padding = '20mm';
  offscreenContainer.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  offscreenContainer.style.fontSize = '14px';
  offscreenContainer.style.lineHeight = '1.6';
  offscreenContainer.style.color = '#374151';
  
  // Apply basic styling to the cloned content
  cleanedClone.style.width = '100%';
  cleanedClone.style.maxWidth = 'none';
  cleanedClone.style.margin = '0';
  cleanedClone.style.padding = '0';
  
  // Ensure text is readable and elements are properly styled
  const allElements = cleanedClone.querySelectorAll('*');
  allElements.forEach(el => {
    // Remove any event listeners or complex attributes that might not serialize well
    el.removeAttribute('data-testid');
    el.removeAttribute('aria-describedby');
    el.removeAttribute('data-reactroot');
    el.removeAttribute('data-react-checksum');
    
    // Remove any data attributes that might trigger React effects
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('data-') && attr.name !== 'data-type') {
        el.removeAttribute(attr.name);
      }
    });
    
    // Remove any absolute positioning that might break layout
    const computedStyle = window.getComputedStyle(el);
    if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') {
      el.style.position = 'static';
    }
    
    // Ensure text is visible
    if (computedStyle.color === 'transparent' || computedStyle.opacity === '0') {
      el.style.color = '#374151';
      el.style.opacity = '1';
    }
    
    // Copy important computed styles to inline styles for better preservation
    if (computedStyle.color && computedStyle.color !== 'rgb(0, 0, 0)') {
      el.style.color = computedStyle.color;
    }
    if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
      el.style.backgroundColor = computedStyle.backgroundColor;
    }
    if (computedStyle.fontSize) {
      el.style.fontSize = computedStyle.fontSize;
    }
    if (computedStyle.fontWeight && computedStyle.fontWeight !== '400') {
      el.style.fontWeight = computedStyle.fontWeight;
    }
    if (computedStyle.textDecoration && computedStyle.textDecoration !== 'none') {
      el.style.textDecoration = computedStyle.textDecoration;
    }
    if (computedStyle.fontStyle && computedStyle.fontStyle !== 'normal') {
      el.style.fontStyle = computedStyle.fontStyle;
    }
  });
  
  offscreenContainer.appendChild(cleanedClone);
  return offscreenContainer;
};

/**
 * Render content to canvas using html2canvas
 */
const renderContentToCanvas = async (offscreenContainer) => {
  // Dynamically import html2canvas
  const { default: html2canvas } = await import('html2canvas');
  
  // Append to document body (required for html2canvas)
  document.body.appendChild(offscreenContainer);
  
  try {
    // Yield control before rendering
    await yieldControl();
    
    // Render with conservative settings and additional options to prevent network requests
    const canvas = await html2canvas(offscreenContainer, {
      scale: 1.25, // Conservative scale to avoid performance issues
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false, // Disable logging to avoid console spam
      width: offscreenContainer.scrollWidth,
      height: offscreenContainer.scrollHeight,
      ignoreElements: (element) => {
        // Ignore any remaining iframes, scripts, or other problematic elements
        return element.tagName === 'IFRAME' || 
               element.tagName === 'SCRIPT' || 
               element.tagName === 'NOSCRIPT' ||
               element.hasAttribute('data-html2canvas-ignore');
      },
      onclone: (clonedDoc) => {
        // Additional cleanup on the cloned document
        const clonedBody = clonedDoc.body;
        if (clonedBody) {
          // Remove any remaining scripts
          const scripts = clonedBody.querySelectorAll('script');
          scripts.forEach(script => script.remove());
          
          // Remove any remaining iframes
          const iframes = clonedBody.querySelectorAll('iframe');
          iframes.forEach(iframe => iframe.remove());
        }
      }
    });
    
    // Yield control after rendering
    await yieldControl();
    
    return canvas;
  } finally {
    // Always clean up the offscreen container
    if (offscreenContainer.parentNode) {
      offscreenContainer.parentNode.removeChild(offscreenContainer);
    }
  }
};

/**
 * Slice canvas into page-sized PNG blobs
 */
const sliceCanvasToPages = async (canvas) => {
  // A4 dimensions in mm and conversion factors
  const a4Width = 210;
  const a4Height = 297;
  const pageMargin = 20; // 20mm margin
  const contentWidth = a4Width - (pageMargin * 2);
  const contentHeight = a4Height - (pageMargin * 2);
  
  // Calculate scaling
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const scaleFactor = (contentWidth * 72 / 25.4) / canvasWidth; // Convert mm to points and scale
  
  // Calculate how many pages we need
  const scaledHeight = canvasHeight * scaleFactor * 25.4 / 72; // Convert back to mm
  const pagesNeeded = Math.ceil(scaledHeight / contentHeight);
  
  const pageBlobs = [];
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  
  for (let page = 0; page < pagesNeeded; page++) {
    // Calculate source rectangle for this page
    const sourceY = page * (contentHeight * 72 / 25.4 / scaleFactor);
    const sourceHeight = Math.min(contentHeight * 72 / 25.4 / scaleFactor, canvasHeight - sourceY);
    
    // Set up temp canvas for this page slice
    tempCanvas.width = canvasWidth;
    tempCanvas.height = sourceHeight;
    
    // Draw the slice
    tempCtx.drawImage(
      canvas,
      0, sourceY, canvasWidth, sourceHeight,
      0, 0, canvasWidth, sourceHeight
    );
    
    // Convert to blob
    const blob = await new Promise(resolve => {
      tempCanvas.toBlob(resolve, 'image/png', 1.0);
    });
    
    pageBlobs.push({
      blob,
      width: canvasWidth,
      height: sourceHeight,
      scaleFactor
    });
    
    // Yield control between pages
    if (page < pagesNeeded - 1) {
      await yieldControl();
    }
  }
  
  return {
    pageBlobs,
    pageGeometry: {
      a4Width,
      a4Height,
      pageMargin,
      contentWidth,
      contentHeight
    }
  };
};

/**
 * Internal helper function to handle the PDF export process
 */
const performPDFExport = async (contentElement, studentName, lessonDateISO) => {
  let offscreenContainer = null;
  let worker = null;
  let workerUrl = null;
  
  try {
    // Step 1: Prepare content for rendering (this now includes cleaning problematic elements)
    offscreenContainer = prepareContentForRender(contentElement);
    
    // Step 2: Render content to canvas on main thread
    const canvas = await renderContentToCanvas(offscreenContainer);
    
    // Step 3: Slice canvas into page-sized blobs
    const { pageBlobs, pageGeometry } = await sliceCanvasToPages(canvas);
    
    // Yield control before starting worker
    await yieldControl();
    
    // Step 4: Create worker from Blob to avoid Vite bundling issues
    const workerBlob = new Blob([jsPdfUmd + '\n' + pdfWorkerScript], { type: 'application/javascript' });
    workerUrl = URL.createObjectURL(workerBlob);
    worker = new Worker(workerUrl);
    
    // Step 5: Handle worker communication
    const result = await new Promise((resolve, reject) => {
       let timeoutId = null;
      // Set up worker message handlers
      worker.onmessage = (e) => {
        const { type, data } = e.data;
        
        if (type === 'PDF_ASSEMBLY_SUCCESS') {
          try {
            // Generate filename
            const filename = generateFilename(data.studentName, data.lessonDateISO);
            
            // Download the file
            downloadFile(data.pdfData, filename);
            
            resolve();
            if (timeoutId) clearTimeout(timeoutId);
          } catch (error) {
            console.error('Error downloading PDF:', error);
            reject(new Error('Failed to download PDF'));
            if (timeoutId) clearTimeout(timeoutId);
          }
        } else if (type === 'PDF_ASSEMBLY_ERROR') {
          console.error('PDF assembly error:', data.error);
          reject(new Error(data.error || 'PDF assembly failed'));
        }
      };
      
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        reject(new Error('PDF worker failed'));
      };
      
      // Send page data to worker for assembly
      worker.postMessage({
        type: 'ASSEMBLE_PDF',
        data: {
          pageBlobs,
          pageGeometry,
          studentName,
          lessonDateISO
        }
      });
      
      // Set a timeout to prevent hanging
      timeoutId = setTimeout(() => {
        reject(new Error('PDF export timed out'));
      }, 60000); // 60 second timeout
    });
    
    return result;
    
  } finally {
    // Cleanup
    if (worker) {
      worker.terminate();
    }
    if (workerUrl) {
      URL.revokeObjectURL(workerUrl);
    }
  }
};

/**
 * Exports a DOM element to PDF using main thread rendering + worker assembly
 * @param {HTMLElement} contentElement - The DOM element containing the note content
 * @param {string} studentName - The student's name
 * @param {string} lessonDateISO - The lesson date in ISO format
 * @returns {Promise<void>}
 */
export const exportNoteToPDF = async (contentElement, studentName, lessonDateISO) => {
  try {
    await performPDFExport(contentElement, studentName, lessonDateISO);
  } catch (error) {
    console.error('Error setting up PDF export:', error);
    throw error;
  }
};